import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, X, AlertCircle, CheckCircle, User, DollarSign, Users, Activity, TrendingUp, Clock, Calendar, CreditCard, Settings } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import BlogManager from './BlogManager';

interface AdvisorApplication {
  id: string;
  display_name: string;
  specialty: string;
  bio: string;
  created_at: string;
  approved: boolean;
  user_email?: string;
}

interface DashboardStats {
  totalRevenue: number;
  monthlyRevenue: number;
  totalClients: number;
  activeClients: number;
  totalAdvisors: number;
  activeAdvisors: number;
  averageRating: number;
  totalReadings: number;
  platformFees: number;
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [applications, setApplications] = useState<AdvisorApplication[]>([]);
  const [stats, setStats] = useState<DashboardStats>({
    totalRevenue: 0,
    monthlyRevenue: 0,
    totalClients: 0,
    activeClients: 0,
    totalAdvisors: 0,
    activeAdvisors: 0,
    averageRating: 0,
    totalReadings: 0,
    platformFees: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'advisors' | 'blog'>('overview');
  const [generatingBlog, setGeneratingBlog] = useState(false);
  const [lastGenerationAttempt, setLastGenerationAttempt] = useState<Date | null>(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    checkAdminAccess();
  }, [user, navigate]);

  useEffect(() => {
    if (user) {
      fetchDashboardData();
      fetchApplications();
    }
  }, [user]);

  const checkAdminAccess = async () => {
    try {
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('admin_user')
        .eq('id', user?.id)
        .single();

      if (profileError || !profile?.admin_user) {
        navigate('/');
        return;
      }
    } catch (err) {
      console.error('Error checking admin access:', err);
      navigate('/');
    }
  };

  const fetchDashboardData = async () => {
    try {
      // Get total revenue and platform fees
      const { data: transactions, error: transactionError } = await supabase
        .from('transactions')
        .select('amount, platform_fee, created_at')
        .order('created_at', { ascending: false });

      if (transactionError) throw transactionError;

      const now = new Date();
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

      const totalRevenue = transactions?.reduce((sum, t) => sum + t.amount, 0) || 0;
      const monthlyRevenue = transactions?.reduce((sum, t) => {
        const transactionDate = new Date(t.created_at);
        return transactionDate >= firstDayOfMonth ? sum + t.amount : sum;
      }, 0) || 0;
      const platformFees = transactions?.reduce((sum, t) => sum + (t.platform_fee || 0), 0) || 0;

      // Get client stats
      const { count: totalClients } = await supabase
        .from('profiles')
        .select('id', { count: 'exact' })
        .eq('user_type', 'client');

      const { count: activeClients } = await supabase
        .from('transactions')
        .select('client_id', { count: 'exact', distinct: true })
        .gte('created_at', firstDayOfMonth.toISOString());

      // Get advisor stats
      const { data: advisors, error: advisorError } = await supabase
        .from('profiles')
        .select('id, rating, total_readings')
        .eq('user_type', 'advisor');

      if (advisorError) throw advisorError;

      const { count: activeAdvisors } = await supabase
        .from('advisor_settings')
        .select('advisor_id', { count: 'exact' })
        .eq('is_available', true);

      const totalReadings = advisors?.reduce((sum, a) => sum + (a.total_readings || 0), 0) || 0;
      const averageRating = advisors?.reduce((sum, a) => sum + (a.rating || 0), 0) / (advisors?.length || 1);

      setStats({
        totalRevenue,
        monthlyRevenue,
        totalClients: totalClients || 0,
        activeClients: activeClients || 0,
        totalAdvisors: advisors?.length || 0,
        activeAdvisors: activeAdvisors || 0,
        averageRating,
        totalReadings,
        platformFees
      });

    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard statistics');
    }
  };

  const fetchApplications = async () => {
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        throw new Error('Failed to get session: ' + sessionError.message);
      }

      if (!session) {
        navigate('/login');
        return;
      }

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-advisor-applications`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch applications');
      }

      const data = await response.json();
      
      if (!Array.isArray(data)) {
        throw new Error('Invalid response format from server');
      }

      setApplications(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching applications:', err);
      setError(
        err instanceof Error 
          ? `Failed to load advisor applications: ${err.message}` 
          : 'Failed to load advisor applications. Please try again.'
      );
      setApplications([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApproval = async (advisorId: string, approved: boolean) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ 
          approved,
          updated_at: new Date().toISOString()
        })
        .eq('id', advisorId);

      if (error) throw error;

      setApplications(apps => 
        apps.map(app => 
          app.id === advisorId ? { ...app, approved } : app
        )
      );

      setSuccess(`Advisor ${approved ? 'approved' : 'rejected'} successfully`);
      setTimeout(() => setSuccess(null), 3000);
      
      // Refresh dashboard data
      fetchDashboardData();
    } catch (err) {
      console.error('Error updating approval status:', err);
      setError('Failed to update approval status');
    }
  };

  const navigateToStripeWebhook = () => {
    navigate('/stripe-webhook');
  };

  const canGenerateBlogPost = () => {
    if (!lastGenerationAttempt) return true;
    
    // Check if at least 5 minutes have passed since the last attempt
    const timeSinceLastAttempt = Date.now() - lastGenerationAttempt.getTime();
    return timeSinceLastAttempt >= 5 * 60 * 1000; // 5 minutes in milliseconds
  };

  const generateDailyBlogPost = async () => {
    try {
      if (!canGenerateBlogPost()) {
        setError('Please wait 5 minutes between blog post generation attempts');
        return;
      }

      setError(null);
      setSuccess(null);
      setGeneratingBlog(true);
      setLastGenerationAttempt(new Date());
      
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/daily-blog-post`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 429) {
          throw new Error('OpenAI API quota exceeded. Please try again later or contact support to upgrade your plan.');
        }
        throw new Error(errorData.error || 'Failed to generate blog post');
      }

      const data = await response.json();
      
      if (data.success) {
        setSuccess('Blog post generated successfully!');
      } else {
        throw new Error(data.message || 'Failed to generate blog post');
      }
    } catch (err) {
      console.error('Error generating blog post:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate blog post');
    } finally {
      setGeneratingBlog(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-purple-900 text-white py-8">
        <div className="container mx-auto px-4">
          <h1 className="text-3xl font-bold">Admin Dashboard</h1>
          <p className="mt-2 text-purple-200">Platform Overview and Management</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {error && (
          <div className="mb-6 p-4 bg-red-50 rounded-lg flex items-center text-red-700">
            <AlertCircle className="w-5 h-5 mr-2" />
            <p>{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-50 rounded-lg flex items-center text-green-700">
            <CheckCircle className="w-5 h-5 mr-2" />
            <p>{success}</p>
          </div>
        )}

        {/* Admin Tools */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <h2 className="text-xl font-semibold mb-4">Admin Tools</h2>
          <div className="flex flex-wrap gap-4">
            <button
              onClick={navigateToStripeWebhook}
              className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center"
            >
              <Settings className="w-5 h-5 mr-2" />
              Stripe Webhook Configuration
            </button>
            
            <button
              onClick={generateDailyBlogPost}
              disabled={generatingBlog || !canGenerateBlogPost()}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {generatingBlog ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Generating...
                </>
              ) : !canGenerateBlogPost() ? (
                <>
                  <Clock className="w-5 h-5 mr-2" />
                  Please wait...
                </>
              ) : (
                <>
                  <Calendar className="w-5 h-5 mr-2" />
                  Generate Daily Blog Post
                </>
              )}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('advisors')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'advisors'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Advisor Applications
            </button>
            <button
              onClick={() => setActiveTab('blog')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'blog'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Blog Manager
            </button>
          </div>
        </div>

        {activeTab === 'overview' && (
          <>
            {/* Dashboard Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-gray-500 text-sm">Total Revenue</h3>
                  <DollarSign className="text-purple-600 w-6 h-6" />
                </div>
                <p className="text-2xl font-bold">${stats.totalRevenue.toFixed(2)}</p>
                <p className="text-sm text-gray-500 mt-2">
                  Platform Fees: ${stats.platformFees.toFixed(2)}
                </p>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-gray-500 text-sm">Monthly Revenue</h3>
                  <TrendingUp className="text-green-600 w-6 h-6" />
                </div>
                <p className="text-2xl font-bold">${stats.monthlyRevenue.toFixed(2)}</p>
                <p className="text-sm text-gray-500 mt-2">Current Month</p>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-gray-500 text-sm">Total Readings</h3>
                  <Activity className="text-blue-600 w-6 h-6" />
                </div>
                <p className="text-2xl font-bold">{stats.totalReadings}</p>
                <p className="text-sm text-gray-500 mt-2">
                  Avg Rating: {stats.averageRating.toFixed(1)}⭐
                </p>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-gray-500 text-sm">Active Users</h3>
                  <Users className="text-indigo-600 w-6 h-6" />
                </div>
                <p className="text-2xl font-bold">{stats.activeClients}</p>
                <p className="text-sm text-gray-500 mt-2">
                  Total Clients: {stats.totalClients}
                </p>
              </div>
            </div>

            {/* Advisor Stats */}
            <div className="bg-white rounded-lg shadow mb-8">
              <div className="p-6 border-b">
                <h2 className="text-xl font-semibold">Advisor Statistics</h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="flex items-center">
                    <User className="text-purple-600 w-8 h-8 mr-4" />
                    <div>
                      <p className="text-2xl font-bold">{stats.totalAdvisors}</p>
                      <p className="text-gray-500">Total Advisors</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Clock className="text-green-600 w-8 h-8 mr-4" />
                    <div>
                      <p className="text-2xl font-bold">{stats.activeAdvisors}</p>
                      <p className="text-gray-500">Currently Online</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <CreditCard className="text-blue-600 w-8 h-8 mr-4" />
                    <div>
                      <p className="text-2xl font-bold">${(stats.totalRevenue - stats.platformFees).toFixed(2)}</p>
                      <p className="text-gray-500">Advisor Earnings</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {activeTab === 'advisors' && (
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold">Advisor Applications</h2>
            </div>

            {applications.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                {error ? 'Failed to load applications' : 'No pending applications'}
              </div>
            ) : (
              <div className="divide-y">
                {applications.map((application) => (
                  <div key={application.id} className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <User className="w-5 h-5 text-gray-400 mr-2" />
                          <h3 className="text-lg font-semibold">{application.display_name}</h3>
                          <span className={`ml-3 px-2 py-1 text-xs font-medium rounded-full ${
                            application.approved
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {application.approved ? 'Approved' : 'Pending'}
                          </span>
                        </div>

                        <div className="mb-4">
                          <p className="text-sm text-gray-600">Email</p>
                          <p className="font-medium">{application.user_email || 'Email not available'}</p>
                        </div>

                        <div className="mb-4">
                          <p className="text-sm text-gray-600">Specialty</p>
                          <p className="font-medium">{application.specialty}</p>
                        </div>

                        <div>
                          <p className="text-sm text-gray-600">Bio</p>
                          <p className="text-gray-800">{application.bio}</p>
                        </div>
                      </div>

                      <div className="ml-6 flex space-x-2">
                        {!application.approved && (
                          <button
                            onClick={() => handleApproval(application.id, true)}
                            className="bg-green-500 text-white p-2 rounded-lg hover:bg-green-600 transition-colors"
                            title="Approve"
                          >
                            <Check className="w-5 h-5" />
                          </button>
                        )}
                        <button
                          onClick={() => handleApproval(application.id, false)}
                          className="bg-red-500 text-white p-2 rounded-lg hover:bg-red-600 transition-colors"
                          title="Reject"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'blog' && (
          <BlogManager />
        )}
      </div>
    </div>
  );
}